import { Button } from "primereact/button";
import { DigitNumbers, DoubleDigitNumbers, SingleDigitNumbers, TimesTableNumbers, TripleDigitNumbers } from "../Constants.ts";
import React from "react";
import { useSettingsActions } from "./SettingsStore.tsx";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import 'primeicons/primeicons.css';
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

// Reads settings only when a button is pressed, so it renders from its props alone,
// and of `range` only its last value (which picks the labels). A tick builds a new
// array with the same last value; comparing the array would redraw four buttons for it.
export const RangeButtons = React.memo(function RangeButtons({rangeGroup, range}: {rangeGroup: number, range: number[]}) {
    const {getSettings, setSettings} = useSettingsActions();
    const t = useTranslation();

    function handleNumberRangeButtonClick(choice: 'low' | 'high' | 'all' | 'clear', rangeGroup: number) {
        let updatedSettings = JSON.parse(JSON.stringify(getSettings()));
        let range = updatedSettings.NumberRanges.filter(x => x.Id === rangeGroup)[0];
        switch(choice) {
            case 'low': {
                const digitNumbers = checkNumberRange(range.NumberRange);
                range.NumberRangeSelection = digitNumbers.LowRange;
                break;            
            }
            case 'high': {
                const digitNumbers = checkNumberRange(range.NumberRange);
                range.NumberRangeSelection = digitNumbers.HighRange;
                break;
            }
            case 'all': {
                range.NumberRangeSelection = range.NumberRange;
                break;
            }
            default: {
                range.NumberRangeSelection = [];
                break;
            }
        }
        setSettings(updatedSettings);
    }

    let labels: string[] = [];
    switch(range[range.length - 1]) {
        
        case DoubleDigitNumbers.Range[DoubleDigitNumbers.Range.length - 1]: {
            labels = ['10-40', '50-90'];
            break;
        }
        case TripleDigitNumbers.Range[TripleDigitNumbers.Range.length - 1]: {
            labels = ['100-400', '500-900'];
            break;
        }
        case TimesTableNumbers.Range[TimesTableNumbers.Range.length - 1]: {
            labels = ['0-6', '7-12'];
            break;
        }
        default : {
            labels = ['0-4', '5-9'];
            break;
        }
    }
    // "All" and "Clear" read identically on both ranges without the suffix, so
    // every button says which value range it acts on.
    const forRange = format(t('settings.forValueRangeSuffix'), { id: rangeGroup });
    // Styled as a row of text links under the number toggles (ADR 0061 §3);
    // still buttons, since they change state rather than navigate.
    const rangeButtons = (
        <div className='rangeLinks'>
            <Button  id={`lowRange-${rangeGroup}`} aria-label={`${t('settings.selectPrefix')}${labels[0]}${forRange}`}
                onClick={() => handleNumberRangeButtonClick('low', rangeGroup)}>{labels[0]}</Button >
            <Button  id={`highRange-${rangeGroup}`} aria-label={`${t('settings.selectPrefix')}${labels[1]}${forRange}`}
                onClick={() => handleNumberRangeButtonClick('high', rangeGroup)}>{labels[1]}</Button >
            <Button  id={`allRange-${rangeGroup}`} aria-label={`${t('settings.selectAllNumbers')}${forRange}`}
                onClick={() => handleNumberRangeButtonClick('all', rangeGroup)}>{t('settings.all')}</Button >
            <Button  id={`clearRange-${rangeGroup}`} aria-label={`${t('settings.clearAllNumbers')}${forRange}`}
                onClick={() => handleNumberRangeButtonClick('clear', rangeGroup)}>{t('settings.clear')}</Button >
        </div>
    );
    return rangeButtons;
}, (before, after) =>
    before.rangeGroup === after.rangeGroup &&
    before.range[before.range.length - 1] === after.range[after.range.length - 1]);

function checkNumberRange(range) : DigitNumbers {
    switch(range[0].toString().length) {            
        case 2: {
            return DoubleDigitNumbers;
        }
        case 3: {
            return TripleDigitNumbers;
        }
        default: {
            // The one-digit range is 0-9 or, on a times-table sheet, 0-12
            // (ADR 0064); its last value says which.
            return range[range.length - 1] === TimesTableNumbers.Range[TimesTableNumbers.Range.length - 1]
                ? TimesTableNumbers
                : SingleDigitNumbers;
        }
    }
}

  
   